'use client'
import React, { FC, HTMLProps, useEffect } from 'react'
import { ChangeEvent, useState } from 'react'
import clsx from 'classnames'
import { useLocale } from 'use-intl'
import { useTranslations } from 'next-intl'
import { Button, Text } from '@/shared/ui'
import SearchSVG from '@/public/header-search.svg'
import CrossSVG from '@/public/input-cross.svg'
import { useDebounce } from '@/shared/utils'
import { SearchItemSkeleton } from '@/sections/search/searchItem/searchItemSkeleton'
import { DialogContent } from './dialog'
import style from './search.module.scss'
import { SearchItem } from './searchItem/searchItem'

export const Search: FC<HTMLProps<HTMLInputElement>> = ({ ...inputProps }) => {
  const [inputValue, setInputValue] = useState('')
  const [searchItems, setSearchItems] = useState([])
  const [isHasResponse, setIsHasResponse] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const translate = useTranslations()
  const debauncedValue = useDebounce(inputValue)
  const currentLocale = useLocale()

  useEffect(() => {
    const data = Promise.all([
      fetch(`/api/search?search=${debauncedValue}&locale=${currentLocale}`)
        .then(res => res.json())
        .catch(err => console.error(err)),
    ]).then(response => {
      setIsLoading(false)

      const res = response.flatMap(el => el)
      if (res.length === 0) {
        setIsHasResponse(translate('emptyMessage'))
        setSearchItems([])
      } else {
        setIsHasResponse('')
        setSearchItems(...response)
      }
    })
  }, [debauncedValue, currentLocale])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.currentTarget.value)
    setIsLoading(true)
  }

  const handleClear = () => {
    setInputValue('')
  }

  return (
    <DialogContent className={style.dialogContent}>
      <div className={clsx(style.inputWrapper, { [style.active]: !!inputValue })}>
        <span className={clsx(style.icon, style.search)}>
          <SearchSVG />
        </span>
        <input
          className={clsx('text', 'text_type_t1 text_color_dark', style.input)}
          autoFocus={false}
          value={inputValue}
          onChange={handleChange}
          onClick={e => e.stopPropagation()}
          {...inputProps}
        />
        {inputValue && (
          <Button
            type="ghost"
            onClick={e => {
              e.stopPropagation()
              handleClear()
            }}
            className={clsx(style.icon, style.cross)}
          >
            <CrossSVG />
          </Button>
        )}
      </div>

      {!!inputValue &&
        (isLoading ? (
          <ul className={style.searchResultList}>
            <SearchItemSkeleton />
          </ul>
        ) : searchItems.length === 0 && isHasResponse ? (
          <ul className={style.searchResultList}>
            <Text color="dark" className={style.emptyMessage}>
              {isHasResponse}
            </Text>
          </ul>
        ) : (
          <ul className={style.searchResultList}>
            {searchItems.map(({ category, title, content, url }, index) => (
              <SearchItem
                setInputValue={setInputValue}
                key={index}
                category={category}
                title={title}
                content={content}
                url={url}
              />
            ))}
          </ul>
        ))}
    </DialogContent>
  )
}
